import React from 'react';

import { track, useAutoNavigate, useQuery, useReturnToPath } from '@betterment/js-runtime';
import { Icon } from '@betterment/samba-react';
import {
  Box,
  CenteredWorkflowLayout,
  Flex,
  HR,
  Heading,
  ListRowButton,
  WorkflowHeader,
} from '@betterment/style-closet-react';
import _get from 'lodash/get';
import { useNavigate } from 'react-router-dom';

import { useAccount } from '../Checking/components/AccountProvider';
import FullPageLoader from '../Shared/components/FullPageLoader';
import { TransferSourceAccountResponse } from '../Shared/data';
import { getId, isIndividualCash } from '../Shared/models/betterment-account';
import { locationAssign } from '../Shared/path-helpers';
import { urlBuilder } from '../Shared/urlBuilder';

const Controller: React.FC = () => {
  const { data: sourceAccountsData, loading: isSourceAccountsRequestLoading } = useQuery<TransferSourceAccountResponse>(
    urlBuilder.apiV2TransferSourceAccounts.absolute()
  );
  const cashReserveAccount = sourceAccountsData?.availableSourceAccounts?.find(account => isIndividualCash(account));
  const shouldShowCashReserveAccount = _get(cashReserveAccount, 'displayBalanceCents', 0) > 0;
  const { account } = useAccount();
  const navigateRouter = useNavigate();
  const navigate = useAutoNavigate();
  const returnToPath = useReturnToPath(urlBuilder.getAppChecking.absolute());

  if (isSourceAccountsRequestLoading) return <FullPageLoader />;
  return (
    <CenteredWorkflowLayout variant="six-column">
      <CenteredWorkflowLayout.Slot name="header">
        <WorkflowHeader
          backButtonHandler={() => navigateRouter(-1)}
          title="Checking"
          closeHandler={() => locationAssign(returnToPath)}
        />
      </CenteredWorkflowLayout.Slot>
      <CenteredWorkflowLayout.Slot name="content">
        <Flex justifyContent="left" mb="1" pb="8">
          <Heading variant="primary">How do you want to fund your account?</Heading>
        </Flex>
        <HR />
        <Box bg="bmtWhite">
          <ListRowButton
            variant="expanded"
            verticalAlign="top"
            leadingTitle="Deposit from a bank account"
            leadingSubtitle="Connect your external bank account to transfer funds into Betterment Checking"
            trailingIcon={<Icon name="CaretRight" size="small" color="icon" />}
            leadingIcon={<Icon name="Bank" size="small" color="icon" />}
            onPress={() => {
              void navigate(urlBuilder.getAppCheckingAccountDepositSetup.absolute({ accountId: account.id }));
            }}
          />
        </Box>
        <HR />
        <Box bg="bmtWhite">
          <ListRowButton
            variant="expanded"
            verticalAlign="top"
            leadingTitle="Set up direct deposit"
            leadingSubtitle="Copy your account and routing numbers or download a pre-filled form"
            trailingIcon={<Icon name="CaretRight" size="small" color="icon" />}
            leadingIcon={<Icon name="DepositCheck" size="small" color="icon" />}
            onPress={() => {
              track('ElementClicked', { name: 'SetUpDirectDeposit', module: 'SetUpDirectDeposit' });
              void navigate(urlBuilder.getAppCheckingAccountDirectDeposit.absolute({ accountId: account.id }));
            }}
          />
        </Box>
        <HR />
        <>
          {shouldShowCashReserveAccount && cashReserveAccount && (
            <>
              <Box bg="bmtWhite">
                <ListRowButton
                  variant="expanded"
                  verticalAlign="top"
                  leadingTitle="Transfer from Cash Reserve"
                  leadingSubtitle="Move funds from your Cash Reserve to Betterment Checking"
                  trailingIcon={<Icon name="CaretRight" size="small" color="icon" />}
                  leadingIcon={<Icon name="Transfer" size="small" color="icon" />}
                  onPress={() => {
                    locationAssign(
                      urlBuilder.showSubAccountTransferSetup.absolute({
                        subAccountId: getId(cashReserveAccount)!,
                        return_to: urlBuilder.getAppChecking.absolute(),
                      })
                    );
                  }}
                />
              </Box>
              <HR />{' '}
            </>
          )}
        </>
      </CenteredWorkflowLayout.Slot>
    </CenteredWorkflowLayout>
  );
};

export default Controller;
